import React, { useState } from 'react';
import { Upload, DollarSign, TrendingUp, TrendingDown, FileText, RefreshCw, Info, Download } from 'lucide-react';

const TransactionProfitCalculator = () => {
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [fileName, setFileName] = useState('');
  const [debugInfo, setDebugInfo] = useState(null);
  const [selectedSymbol, setSelectedSymbol] = useState(null);

  const resetCalculator = () => {
    setResults(null);
    setError('');
    setFileName('');
    setDebugInfo(null);
    setSelectedSymbol(null);
  };

  const downloadSampleCSV = () => {
    const csvContent = `Transaction Date;Transaction Type;Symbol;Quantity;Price;Commission;Amount
01/05/2025;Buy;AAPL;100;150.25;6.95;-15,031.95
01/08/2025;Buy;TSLA;50;245.80;6.95;-12,296.95
01/10/2025;Sell;AAPL;30;155.30;6.95;4,652.05
01/12/2025;Buy;NVDA;75;495.60;6.95;-37,226.95
01/15/2025;Buy;MSFT;80;380.40;6.95;-30,438.95
01/18/2025;Sell;TSLA;25;252.40;6.95;6,303.05
01/20/2025;Buy;GOOGL;40;142.75;6.95;-5,716.95
01/22/2025;Buy;AMD;200;125.30;6.95;-25,066.95
01/25/2025;Sell;NVDA;30;510.20;6.95;15,299.05
01/28/2025;Buy;META;35;485.90;6.95;-17,013.45
02/01/2025;Sell;MSFT;40;385.60;6.95;15,417.05
02/03/2025;Buy;AAPL;60;148.75;6.95;-8,931.95
02/05/2025;Sell;GOOGL;20;145.80;6.95;2,909.05
02/08/2025;Buy;AMD;150;128.45;6.95;-19,274.45
02/10/2025;Sell;META;15;495.75;6.95;7,429.30
02/12/2025;Buy;TSLA;40;238.90;6.95;-9,562.95
02/15/2025;Sell;AMD;100;132.80;6.95;13,273.05
02/18/2025;Buy;NVDA;50;502.30;6.95;-25,121.95
02/20/2025;Sell;AAPL;50;158.90;6.95;7,938.05`;

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', 'sample_transactions.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setLoading(true);
    setError('');
    setResults(null);
    setDebugInfo(null);
    setFileName(file.name);

    try {
      const text = await file.text();
      const lines = text.split('\n').filter(line => line.trim());
      
      if (lines.length < 2) {
        throw new Error('CSV file is empty or invalid');
      }

      const parseCSVLine = (line) => {
        const values = [];
        let current = '';
        let inQuotes = false;
        
        for (let i = 0; i < line.length; i++) {
          const char = line[i];
          
          if (char === '"') {
            inQuotes = !inQuotes;
          } else if (char === ';' && !inQuotes) {
            values.push(current.trim());
            current = '';
          } else {
            current += char;
          }
        }
        values.push(current.trim());
        
        while (values.length > 0 && values[values.length - 1] === '') {
          values.pop();
        }
        
        return values;
      };

      const headers = parseCSVLine(lines[0]);
      const data = [];
      
      for (let i = 1; i < lines.length; i++) {
        const values = parseCSVLine(lines[i]);
        if (values.length >= headers.length && values.some(v => v !== '')) {
          const row = {};
          headers.forEach((header, idx) => {
            row[header] = values[idx] || '';
          });
          data.push(row);
        }
      }

      setDebugInfo({
        headers: headers,
        sampleRows: data.slice(0, 5).map(row => ({
          date: row['Transaction Date'],
          type: row['Transaction Type'],
          symbol: row['Symbol'],
          quantity: row['Quantity'],
          price: row['Price'],
          amount: row['Amount']
        })),
        totalRows: data.length
      });

      const cleanNumber = (str) => {
        if (!str) return 0;
        return parseFloat(str.toString().replace(/,/g, '')) || 0;
      };

      const transactions = data.map(row => {
        const type = (row['Transaction Type'] || '').trim();
        return {
          date: row['Transaction Date'],
          type: type,
          symbol: row['Symbol'],
          quantity: cleanNumber(row['Quantity']),
          price: cleanNumber(row['Price']),
          commission: cleanNumber(row['Commission']),
          amount: cleanNumber(row['Amount'])
        };
      }).filter(t => t.symbol && t.type);

      if (transactions.length === 0) {
        throw new Error('No valid transactions found. Please check that your CSV has columns: Transaction Date;Transaction Type;Symbol;Quantity;Price;Commission;Amount (separated by semicolons)');
      }

      transactions.sort((a, b) => new Date(a.date) - new Date(b.date));

      const symbolGroups = {};
      const symbolResults = [];
      const missingTransactions = [];
      let totalCapitalGain = 0;
      let totalCommissions = 0;

      transactions.forEach(t => {
        if (!symbolGroups[t.symbol]) {
          symbolGroups[t.symbol] = {
            totalACB: 0,
            totalShares: 0,
            totalBuyAmount: 0,
            totalSellAmount: 0,
            totalCapitalGain: 0,
            totalCommissions: 0,
            buyCount: 0,
            sellCount: 0,
            totalBuyShares: 0,
            totalSellShares: 0,
            transactions: [],
            missingBuys: []
          };
        }

        const sym = symbolGroups[t.symbol];
        const cost = Math.abs(t.amount) + t.commission;

        if (t.type === 'Buy') {
          sym.totalACB += cost;
          sym.totalShares += t.quantity;
          sym.totalBuyAmount += Math.abs(t.amount);
          sym.totalCommissions += t.commission;
          sym.buyCount += 1;
          sym.totalBuyShares += t.quantity;
          
          sym.transactions.push({
            date: t.date,
            type: 'Buy',
            quantity: t.quantity,
            price: t.price,
            cost: cost,
            acbPerShare: sym.totalShares > 0 ? sym.totalACB / sym.totalShares : 0,
            totalACB: sym.totalACB,
            totalShares: sym.totalShares
          });

        } else if (t.type === 'Sell') {
          const currentShares = sym.totalShares;
          if (t.quantity > currentShares) {
            const shortage = t.quantity - currentShares;
            sym.missingBuys.push({
              date: t.date,
              symbol: t.symbol,
              sellQuantity: t.quantity,
              ownedShares: currentShares,
              shortageShares: shortage
            });
            missingTransactions.push({
              date: t.date,
              symbol: t.symbol,
              sellQuantity: t.quantity,
              ownedShares: currentShares,
              shortageShares: shortage
            });
          }
          
          const acbPerShare = currentShares > 0 ? sym.totalACB / currentShares : 0;
          const proceeds = Math.abs(t.amount) - t.commission;
          const sharesToSell = Math.min(t.quantity, currentShares);
          const acbOfSoldShares = acbPerShare * sharesToSell;
          const capitalGain = proceeds - acbOfSoldShares;
          
          sym.totalACB -= acbOfSoldShares;
          sym.totalShares -= sharesToSell;
          sym.totalSellAmount += Math.abs(t.amount);
          sym.totalCapitalGain += capitalGain;
          sym.totalCommissions += t.commission;
          sym.sellCount += 1;
          sym.totalSellShares += t.quantity;
          
          sym.transactions.push({
            date: t.date,
            type: 'Sell',
            quantity: t.quantity,
            price: t.price,
            proceeds: proceeds,
            acbPerShare: acbPerShare,
            acbOfSoldShares: acbOfSoldShares,
            capitalGain: capitalGain,
            totalACB: sym.totalACB,
            totalShares: sym.totalShares,
            warning: t.quantity > currentShares ? 'Insufficient shares' : null
          });
        }
      });

      Object.keys(symbolGroups).forEach(symbol => {
        const group = symbolGroups[symbol];
        
        symbolResults.push({
          symbol: symbol,
          buyCount: group.buyCount,
          sellCount: group.sellCount,
          totalBuyShares: group.totalBuyShares,
          totalSellShares: group.totalSellShares,
          totalBuyAmount: group.totalBuyAmount,
          totalSellAmount: group.totalSellAmount,
          commissions: group.totalCommissions,
          capitalGain: group.totalCapitalGain,
          remainingShares: group.totalShares,
          remainingACB: group.totalACB,
          acbPerShare: group.totalShares > 0 ? group.totalACB / group.totalShares : 0,
          transactions: group.transactions,
          hasMissingBuys: group.missingBuys.length > 0
        });

        totalCapitalGain += group.totalCapitalGain;
        totalCommissions += group.totalCommissions;
      });

      symbolResults.sort((a, b) => b.capitalGain - a.capitalGain);

      const totalBuys = transactions.filter(t => t.type === 'Buy').length;
      const totalSells = transactions.filter(t => t.type === 'Sell').length;

      setResults({
        symbolResults: symbolResults,
        totalCapitalGain: totalCapitalGain,
        totalCommissions: totalCommissions,
        totalTransactions: transactions.length,
        totalBuys: totalBuys,
        totalSells: totalSells,
        missingTransactions: missingTransactions
      });

    } catch (err) {
      setError('Error processing file: ' + err.message);
    } finally {
      setLoading(false);
      e.target.value = '';
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(value);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (isNaN(date)) return dateStr;
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const year = date.getFullYear().toString().slice(-2);
    return month + '/' + day + '/' + year;
  };

  const DetailModal = () => {
    if (!selectedSymbol || !results) return null;
    
    const symbolData = results.symbolResults.find(r => r.symbol === selectedSymbol);
    if (!symbolData) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
          <div className="bg-indigo-600 px-6 py-4 flex items-center justify-between">
            <h2 className="text-xl font-bold text-white">
              {selectedSymbol} - Transaction Details (ACB Method)
            </h2>
            <button
              onClick={() => setSelectedSymbol(null)}
              className="text-white hover:bg-indigo-700 rounded-full p-2"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
            <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-bold text-blue-900 mb-2">How ACB Works:</h3>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• <strong>Buy:</strong> Total ACB = Previous ACB + (Amount + Commission)</li>
                <li>• <strong>ACB per Share:</strong> Total ACB ÷ Total Shares Owned</li>
                <li>• <strong>Sell:</strong> Capital Gain = Proceeds - (ACB per Share × Shares Sold)</li>
                <li>• <strong>After Sell:</strong> Total ACB reduces by (ACB per Share × Shares Sold)</li>
              </ul>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead className="bg-gray-100 sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left border font-semibold">Date</th>
                    <th className="px-3 py-2 text-left border font-semibold">Type</th>
                    <th className="px-3 py-2 text-right border font-semibold">Quantity</th>
                    <th className="px-3 py-2 text-right border font-semibold">Price</th>
                    <th className="px-3 py-2 text-right border font-semibold">Amount</th>
                    <th className="px-3 py-2 text-right border font-semibold">ACB/Share</th>
                    <th className="px-3 py-2 text-right border font-semibold">ACB Used</th>
                    <th className="px-3 py-2 text-right border font-semibold">Cap Gain</th>
                    <th className="px-3 py-2 text-right border font-semibold">Total ACB</th>
                    <th className="px-3 py-2 text-right border font-semibold">Tot Shares</th>
                  </tr>
                </thead>
                <tbody>
                  {symbolData.transactions.map((txn, idx) => (
                    <tr key={idx} className={txn.type === 'Buy' ? 'bg-green-50' : 'bg-red-50'}>
                      <td className="px-3 py-2 border text-xs">{formatDate(txn.date)}</td>
                      <td className="px-3 py-2 border font-medium">
                        <span className={txn.type === 'Buy' ? 'text-green-700' : 'text-red-700'}>
                          {txn.type}
                        </span>
                      </td>
                      <td className="px-3 py-2 border text-right">{Math.round(txn.quantity)}</td>
                      <td className="px-3 py-2 border text-right">{formatCurrency(txn.price)}</td>
                      <td className="px-3 py-2 border text-right">
                        {txn.type === 'Buy' ? formatCurrency(txn.cost) : formatCurrency(txn.proceeds)}
                      </td>
                      <td className="px-3 py-2 border text-right font-medium">
                        {formatCurrency(txn.acbPerShare)}
                      </td>
                      <td className="px-3 py-2 border text-right">
                        {txn.type === 'Sell' ? formatCurrency(txn.acbOfSoldShares) : '-'}
                      </td>
                      <td className="px-3 py-2 border text-right font-bold">
                        {txn.type === 'Sell' ? (
                          <span className={txn.capitalGain >= 0 ? 'text-green-700' : 'text-red-700'}>
                            {formatCurrency(txn.capitalGain)}
                          </span>
                        ) : '-'}
                      </td>
                      <td className="px-3 py-2 border text-right font-medium bg-yellow-50">
                        {formatCurrency(txn.totalACB)}
                      </td>
                      <td className="px-3 py-2 border text-right font-medium bg-yellow-50">
                        {Math.round(txn.totalShares)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            <div className="mt-4 bg-gray-50 rounded-lg p-4">
              <h3 className="font-bold text-gray-900 mb-2">Summary:</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div>
                  <p className="text-gray-600">Total Bought:</p>
                  <p className="font-bold text-green-700">{Math.round(symbolData.totalBuyShares)} shares</p>
                </div>
                <div>
                  <p className="text-gray-600">Total Sold:</p>
                  <p className="font-bold text-red-700">{Math.round(symbolData.totalSellShares)} shares</p>
                </div>
                <div>
                  <p className="text-gray-600">Remaining:</p>
                  <p className="font-bold text-blue-700">{Math.round(symbolData.remainingShares)} shares</p>
                </div>
                <div>
                  <p className="text-gray-600">Total Capital Gain:</p>
                  <p className={'font-bold ' + (symbolData.capitalGain >= 0 ? 'text-green-700' : 'text-red-700')}>
                    {formatCurrency(symbolData.capitalGain)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-8">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <DollarSign className="w-8 h-8 text-indigo-600" />
              <h1 className="text-3xl font-bold text-gray-800">ACB Capital Gains Calculator</h1>
            </div>
            {(results || error) && (
              <button
                onClick={resetCalculator}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Reset
              </button>
            )}
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-2">
              <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-blue-800">
                <p className="font-semibold mb-1">Adjusted Cost Base (ACB) Method</p>
                <p>CSV Format: Semicolon (;) delimiter | Date: MM/DD/YYYY | Numbers with commas</p>
              </div>
            </div>
          </div>

          <div className="mb-8">
            <input
              type="file"
              id="csv-upload"
              className="hidden"
              accept=".csv"
              onChange={handleFileUpload}
            />
            <label htmlFor="csv-upload" className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-indigo-300 rounded-lg cursor-pointer bg-indigo-50 hover:bg-indigo-100 transition-colors">
              <div className="flex flex-col items-center justify-center pt-5 pb-6">
                <Upload className="w-10 h-10 mb-3 text-indigo-600" />
                <p className="mb-2 text-sm text-gray-600">
                  <span className="font-semibold">Click to upload</span> CSV file
                </p>
                <p className="text-xs text-gray-500">
                  {fileName || 'Transaction history CSV (semicolon separated)'}
                </p>
              </div>
            </label>
            
            <div className="mt-4 flex justify-center items-center gap-4">
              <button
                onClick={downloadSampleCSV}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
              >
                <Download className="w-4 h-4" />
                Download Sample CSV
              </button>
              <a 
                href="mailto:lindaurban29@gmail.com"
                className="flex items-center gap-2 text-sm text-gray-600 hover:text-indigo-600 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <span>Need help? lindaurban29@gmail.com</span>
              </a>
            </div>
          </div>

          {loading && (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
              <p className="mt-4 text-gray-600">Processing {fileName}...</p>
              <p className="mt-2 text-sm text-gray-500">Reading CSV data and calculating ACB...</p>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <p className="text-red-800">{error}</p>
            </div>
          )}

          {results && (
            <div className="space-y-6">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <div>
                    <p className="font-semibold text-green-900">File uploaded successfully!</p>
                    <p className="text-sm text-green-700">
                      Processed: <span className="font-medium">{fileName}</span> • 
                      <span className="ml-1">{results.totalTransactions} transactions loaded</span> • 
                      <span className="ml-1">{results.symbolResults.length} symbols found</span>
                    </p>
                  </div>
                </div>
              </div>

              {debugInfo && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <h3 className="font-bold text-gray-900 mb-2">📄 Loaded: {debugInfo.totalRows} rows</h3>
                  <div className="bg-white rounded border p-3 overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="border-b">
                        <tr>
                          <th className="px-2 py-1 text-left">Date</th>
                          <th className="px-2 py-1 text-left">Type</th>
                          <th className="px-2 py-1 text-left">Symbol</th>
                          <th className="px-2 py-1 text-right">Qty</th>
                          <th className="px-2 py-1 text-right">Price</th>
                        </tr>
                      </thead>
                      <tbody>
                        {debugInfo.sampleRows.map((row, idx) => (
                          <tr key={idx} className="border-b">
                            <td className="px-2 py-1">{row.date}</td>
                            <td className="px-2 py-1">{row.type}</td>
                            <td className="px-2 py-1 font-medium">{row.symbol}</td>
                            <td className="px-2 py-1 text-right">{row.quantity}</td>
                            <td className="px-2 py-1 text-right">{row.price}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {results.missingTransactions && results.missingTransactions.length > 0 && (
                <div className="bg-red-50 border-2 border-red-300 rounded-lg p-6">
                  <h3 className="text-lg font-bold text-red-900 mb-2">⚠️ Missing Buy Transactions</h3>
                  <div className="bg-white rounded-lg overflow-hidden border border-red-200">
                    <table className="w-full text-sm">
                      <thead className="bg-red-100">
                        <tr>
                          <th className="px-4 py-2 text-left">Date</th>
                          <th className="px-4 py-2 text-left">Symbol</th>
                          <th className="px-4 py-2 text-right">Shortage</th>
                        </tr>
                      </thead>
                      <tbody>
                        {results.missingTransactions.map((item, idx) => (
                          <tr key={idx}>
                            <td className="px-4 py-2">{formatDate(item.date)}</td>
                            <td className="px-4 py-2 font-medium">{item.symbol}</td>
                            <td className="px-4 py-2 text-right text-red-700 font-bold">
                              {Math.round(item.shortageShares)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-6 border border-green-200">
                  <p className="text-sm text-green-600 font-medium">Total Capital Gain</p>
                  <p className={'text-2xl font-bold mt-1 ' + (results.totalCapitalGain >= 0 ? 'text-green-700' : 'text-red-700')}>
                    {formatCurrency(results.totalCapitalGain)}
                  </p>
                </div>

                <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-6 border border-blue-200">
                  <p className="text-sm text-blue-600 font-medium">Transactions</p>
                  <p className="text-2xl font-bold text-blue-700 mt-1">{results.totalTransactions}</p>
                </div>

                <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-6 border border-purple-200">
                  <p className="text-sm text-purple-600 font-medium">Commissions</p>
                  <p className="text-2xl font-bold text-purple-700 mt-1">
                    {formatCurrency(results.totalCommissions)}
                  </p>
                </div>
              </div>

              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <div className="bg-indigo-600 px-6 py-4">
                  <h2 className="text-xl font-bold text-white">Click symbol for details</h2>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Symbol</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Buy</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Sell</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Remaining</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">ACB/Share</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Cap Gain</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {results.symbolResults.map((result, idx) => (
                        <tr 
                          key={idx} 
                          className={'hover:bg-indigo-50 cursor-pointer transition ' + (result.hasMissingBuys ? 'bg-red-50' : '')}
                          onClick={() => setSelectedSymbol(result.symbol)}
                        >
                          <td className="px-6 py-4">
                            <span className="font-medium text-gray-900">{result.symbol}</span>
                            {result.hasMissingBuys && <span className="ml-2 text-xs text-red-600">⚠️</span>}
                          </td>
                          <td className="px-6 py-4 text-right text-gray-600">{Math.round(result.totalBuyShares)}</td>
                          <td className="px-6 py-4 text-right text-gray-600">{Math.round(result.totalSellShares)}</td>
                          <td className="px-6 py-4 text-right text-gray-600">{Math.round(result.remainingShares)}</td>
                          <td className="px-6 py-4 text-right text-gray-600">{formatCurrency(result.acbPerShare)}</td>
                          <td className={'px-6 py-4 text-right font-semibold ' + (result.capitalGain >= 0 ? 'text-green-600' : 'text-red-600')}>
                            {formatCurrency(result.capitalGain)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
          
          <DetailModal />
        </div>
      </div>
    </div>
  );
};

export default TransactionProfitCalculator;
